import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { getAuthToken } from "@/lib/auth";
import { buildAuthenticatedWebSocketConfig } from "@/lib/websocket";
import { getWsUrl } from "@/config/runtime";
import {
  EMPTY_PROJECT_PRESENCE,
  getProjectPresenceSnapshot,
  reconnectProjectPresence,
  sendProjectPresence,
  subscribeProjectPresence,
} from "@/lib/sharedProjectPresence";
import type { RealtimeConnectionState as ConnectionState } from "@/lib/sharedRealtimeSocket";
import type {
  PresenceUser,
  CursorPosition,
  RowPresence,
  FeatureCommentPayload,
  ProjectWebSocketEvent,
} from "@/types/projectPresence";
export type {
  PresenceUser,
  CursorPosition,
  RowPresence,
  FeatureCommentPayload,
} from "@/types/projectPresence";
export type { RealtimeConnectionState as ConnectionState } from "@/lib/sharedRealtimeSocket";

interface UseProjectWebSocketOptions {
  projectId: number | null | undefined;
  enabled?: boolean;
  onGeometryEvent?: (payload: ProjectWebSocketEvent) => void;
  onCommentEvent?: (comment: FeatureCommentPayload) => void;
}

interface UseProjectWebSocketResult {
  onlineUsers: PresenceUser[];
  cursorPositions: Map<number, CursorPosition>;
  activeRows: Map<string, RowPresence>;
  connected: boolean;
  connectionState: ConnectionState;
  reconnect: () => void;
  sendCursorPosition: (lat: number, lng: number) => void;
  sendRowPresence: (layerId: number, featureId: string | null) => void;
}

export const useProjectWebSocket = ({
  projectId,
  enabled = true,
  onGeometryEvent,
  onCommentEvent,
}: UseProjectWebSocketOptions): UseProjectWebSocketResult => {
  const token = getAuthToken();
  const config = useMemo(() => {
    if (!enabled || !projectId || !token) return null;
    const base =
      getWsUrl();
    return buildAuthenticatedWebSocketConfig(
      `/ws/project/${projectId}/`,
      token,
      base
    );
  }, [enabled, projectId, token]);
  const callbacks = useRef({ onGeometryEvent, onCommentEvent });
  useEffect(() => {
    callbacks.current = { onGeometryEvent, onCommentEvent };
  }, [onGeometryEvent, onCommentEvent]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!config) return () => {};
      return subscribeProjectPresence(config, {
        onStoreChange,
        onEvent: (payload) => {
          if (getAuthToken() !== token) return;
          const kind = payload.type || payload.event;
          if (kind === "comment.new" && payload.comment) {
            callbacks.current.onCommentEvent?.(
              payload.comment as FeatureCommentPayload
            );
          } else if (
            kind === "geometry.created" ||
            kind === "geometry.updated" ||
            kind === "geometry.deleted"
          ) {
            callbacks.current.onGeometryEvent?.(payload);
          }
        },
      });
    },
    [config, token]
  );
  const getSnapshot = useCallback(
    () =>
      config && getAuthToken() === token
        ? getProjectPresenceSnapshot(config)
        : EMPTY_PROJECT_PRESENCE,
    [config, token]
  );
  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => EMPTY_PROJECT_PRESENCE
  );
  const reconnect = useCallback(() => {
    if (config && getAuthToken() === token) reconnectProjectPresence(config);
  }, [config, token]);
  const sendCursorPosition = useCallback(
    (lat: number, lng: number) => {
      if (config && getAuthToken() === token)
        sendProjectPresence(config, { event: "presence.cursor", lat, lng });
    },
    [config, token]
  );
  const sendRowPresence = useCallback(
    (layerId: number, featureId: string | null) => {
      if (config && getAuthToken() === token)
        sendProjectPresence(config, {
          type: "presence.row",
          layer_id: layerId,
          feature_id: featureId,
        });
    },
    [config, token]
  );

  return {
    ...snapshot,
    connected: snapshot.connectionState === "connected",
    reconnect,
    sendCursorPosition,
    sendRowPresence,
  };
};
